import { useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { DISCIPLINARY_ACTIONS_STATUS } from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';

import { isMobileView } from 'constants/isMobileView';

import { BottomDrawer, DrawerContent, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { NestedCheckbox } from 'uiToolkitV1/NestedCheckbox';
import { TextBodyLarge } from 'uiToolkitV1/Typography';

import type { DisciplinaryNoteRow } from '../MyCompanyDisciplinaryActionsGrid';

type NotifyEmployeeConfirmationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedNoteIds: number[];
  rows: DisciplinaryNoteRow[];
  onNotify: (notifyData: { selectedNoteIds: number[] }) => void;
};

export const NotifyEmployeeConfirmationDrawer = ({
  isOpen,
  onClose,
  selectedNoteIds,
  rows,
  onNotify,
}: NotifyEmployeeConfirmationDrawerProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(['MANAGERS', 'EMPLOYEES']);

  const { managersToNotify, employeesToNotify, filteredNotes } = useMemo(() => {
    const managers = new Set<number>();
    const employees = new Set<number>();
    const validNotes = selectedNoteIds
      .map((noteId) => rows.find((row) => row.NOTE_ID === noteId))
      .filter((note): note is DisciplinaryNoteRow => {
        if (!note) return false;
        return (
          note.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED ||
          note.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE
        );
      });

    validNotes.forEach((note) => {
      if (note.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED) {
        managers.add(note.RAISED_FOR.id);
      } else if (note.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE) {
        employees.add(note.RAISED_FOR.id);
      }
    });

    return {
      managersToNotify: managers.size,
      employeesToNotify: employees.size,
      filteredNotes: validNotes,
    };
  }, [selectedNoteIds, rows]);

  const checkboxOptions = [
    {
      key: 'MANAGERS',
      label: 'Managers yet to publish',
      subLabel: `Notify ${managersToNotify} managers for ${filteredNotes.filter((note) => note.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED).length} disciplinary notes`,
    },
    {
      key: 'EMPLOYEES',
      label: 'Employees yet to acknowledge',
      subLabel: `Notify ${employeesToNotify} employees for ${filteredNotes.filter((note) => note.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE).length} disciplinary notes`,
    },
  ];

  const notesToNotify = useMemo(
    () =>
      filteredNotes
        .filter(
          (note) =>
            (selectedOptions.includes('MANAGERS') && note.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED) ||
            (selectedOptions.includes('EMPLOYEES') && note.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE)
        )
        .map((note) => note.NOTE_ID),
    [filteredNotes, selectedOptions]
  );

  const handleNotify = () => {
    onNotify({ selectedNoteIds: notesToNotify });
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerContent p="4px" mr={isMobileView() ? 'auto !important' : undefined}>
        <DrawerHeader onClose={onClose}>{null}</DrawerHeader>
        <Flex flexDir="column" h="full" border="4px solid" borderColor="border.EBEFF3" borderRadius="8px">
          <Flex flex="1" minH="0" flexDir="column" p="8px" gap="12px">
            <Flex pb="8px" borderBottom="1px solid #E7E7E7">
              <TextBodyLarge bold>Notify all</TextBodyLarge>
            </Flex>
            <NestedCheckbox
              options={checkboxOptions}
              selectedOptionsKeys={selectedOptions}
              onChange={setSelectedOptions}
              allOptionsLabel="Notify all pending actions"
              allOptionsSubLabel={`Notify ${managersToNotify + employeesToNotify} people for ${filteredNotes.length} disciplinary notes`}
              onAllChange={() => {}}
              onSubOptionChange={() => {}}
            />
          </Flex>
          <Box
            display="flex"
            justifyContent="space-between"
            h="72px"
            w="full"
            p="16px"
            bg="surface.F5F7F9"
            boxShadow="0px -1px 2px 0px #00000014"
          >
            <Button variant="transparentOutline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleNotify}
              disabled={selectedOptions.length === 0 || notesToNotify.length === 0}
            >
              Notify
            </Button>
          </Box>
        </Flex>
      </DrawerContent>
    </BottomDrawer>
  );
};
